import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { supabase } from "../lib/supabase";

// Route: /affiliates
// Add to App.jsx or router file:
//   import AffiliatesPage from "./pages/AffiliatesPage";
//   <Route path="/affiliates" element={<AffiliatesPage />} />
// Local dev: http://localhost:3000/affiliates

const FONTS_URL =
  "https://fonts.googleapis.com/css2?family=Instrument+Serif:ital@0;1&family=DM+Sans:opsz,wght@9..40,300;9..40,400;9..40,500;9..40,600&display=swap";

function useFonts() {
  useEffect(() => {
    if (document.querySelector('link[href*="Instrument+Serif"]')) return;
    const l = document.createElement("link");
    l.rel = "stylesheet";
    l.href = FONTS_URL;
    document.head.appendChild(l);
  }, []);
}

const T = {
  g700: "#155c36", g600: "#1b7a47", g500: "#1e9254", g400: "#28b566",
  g200: "#a7d9bc", g100: "#d6f0e3", g50: "#eef8f3",
  n900: "#0f1117", n800: "#1c2130", n700: "#2d3549", n600: "#4a5268",
  n400: "#8a93a8", n300: "#b0b8cc", n200: "#d1d5e0", n150: "#e4e7ef",
  n100: "#eef0f5", n50: "#f7f8fa", white: "#ffffff",
  amber: "#d97706", amberBg: "#fffbeb", amberBorder: "#fde68a",
  gold: "#b45309", goldBg: "#fef3c7", goldBorder: "#fde68a",
  serif: "'Instrument Serif', Georgia, serif",
  sans: "'DM Sans', -apple-system, BlinkMacSystemFont, sans-serif",
};

const Icon = ({ name, size = 20, color = "currentColor", strokeWidth = 1.6 }) => {
  const paths = {
    ArrowLeft:    <><path d="M19 12H5"/><path d="m12 19-7-7 7-7"/></>,
    ArrowRight:   <><path d="M5 12h14"/><path d="m12 5 7 7-7 7"/></>,
    Link:         <><path d="M10 13a5 5 0 0 0 7.54.54l3-3a5 5 0 0 0-7.07-7.07l-1.72 1.71"/><path d="M14 11a5 5 0 0 0-7.54-.54l-3 3a5 5 0 0 0 7.07 7.07l1.71-1.71"/></>,
    Users:        <><path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2"/><circle cx="9" cy="7" r="4"/><path d="M23 21v-2a4 4 0 0 0-3-3.87"/><path d="M16 3.13a4 4 0 0 1 0 7.75"/></>,
    Percent:      <><line x1="19" x2="5" y1="5" y2="19"/><circle cx="6.5" cy="6.5" r="2.5"/><circle cx="17.5" cy="17.5" r="2.5"/></>,
    Phone:        <><path d="M22 16.92v3a2 2 0 0 1-2.18 2 19.79 19.79 0 0 1-8.63-3.07A19.5 19.5 0 0 1 4.69 13.5a19.79 19.79 0 0 1-3.07-8.67A2 2 0 0 1 3.34 3h3a2 2 0 0 1 2 1.72c.127.96.361 1.903.7 2.81a2 2 0 0 1-.45 2.11L7.91 10.09a16 16 0 0 0 6 6l1.27-1.27a2 2 0 0 1 2.11-.45c.907.339 1.85.573 2.81.7A2 2 0 0 1 22 16.92z"/></>,
    Wallet:       <><path d="M21 12V7H5a2 2 0 0 1 0-4h14v4"/><path d="M3 5v14a2 2 0 0 0 2 2h16v-5"/><path d="M18 12a2 2 0 0 0 0 4h4v-4z"/></>,
    Share:        <><circle cx="18" cy="5" r="3"/><circle cx="6" cy="12" r="3"/><circle cx="18" cy="19" r="3"/><line x1="8.59" x2="15.42" y1="13.51" y2="17.49"/><line x1="15.41" x2="8.59" y1="6.51" y2="10.49"/></>,
    BarChart:     <><line x1="12" x2="12" y1="20" y2="10"/><line x1="18" x2="18" y1="20" y2="4"/><line x1="6" x2="6" y1="20" y2="16"/></>,
    CheckCircle:  <><circle cx="12" cy="12" r="10"/><path d="m9 12 2 2 4-4"/></>,
    CircleCheck:  <><circle cx="12" cy="12" r="10"/><path d="m9 12 2 2 4-4"/></>,
    Sparkles:     <><path d="m12 3-1.912 5.813a2 2 0 0 1-1.275 1.275L3 12l5.813 1.912a2 2 0 0 1 1.275 1.275L12 21l1.912-5.813a2 2 0 0 1 1.275-1.275L21 12l-5.813-1.912a2 2 0 0 1-1.275-1.275L12 3z"/></>,
    Mail:         <><rect width="20" height="16" x="2" y="4" rx="2"/><path d="m22 7-8.97 5.7a1.94 1.94 0 0 1-2.06 0L2 7"/></>,
    Instagram:    <><rect width="20" height="20" x="2" y="2" rx="5"/><path d="M16 11.37A4 4 0 1 1 12.63 8 4 4 0 0 1 16 11.37z"/><line x1="17.5" x2="17.51" y1="6.5" y2="6.5"/></>,
    Globe:        <><circle cx="12" cy="12" r="10"/><line x1="2" x2="22" y1="12" y2="12"/><path d="M12 2a15.3 15.3 0 0 1 4 10 15.3 15.3 0 0 1-4 10 15.3 15.3 0 0 1-4-10 15.3 15.3 0 0 1 4-10z"/></>,
    Megaphone:    <><path d="m3 11 19-9-9 19-2-8-8-2z"/></>,
    Clock:        <><circle cx="12" cy="12" r="10"/><polyline points="12 6 12 12 16 14"/></>,
    Shield:       <><path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z"/></>,
    Info:         <><circle cx="12" cy="12" r="10"/><path d="M12 16v-4M12 8h.01"/></>,
    Star:         <><polygon points="12 2 15.09 8.26 22 9.27 17 14.14 18.18 21.02 12 17.77 5.82 21.02 7 14.14 2 9.27 8.91 8.26 12 2"/></>,
    TrendingUp:   <><polyline points="22 7 13.5 15.5 8.5 10.5 2 17"/><polyline points="16 7 22 7 22 13"/></>,
    Gift:         <><polyline points="20 12 20 22 4 22 4 12"/><rect width="20" height="5" x="2" y="7" rx="1"/><path d="M12 22V7"/><path d="M12 7H7.5a2.5 2.5 0 0 1 0-5C11 2 12 7 12 7z"/><path d="M12 7h4.5a2.5 2.5 0 0 0 0-5C13 2 12 7 12 7z"/></>,
    Award:        <><circle cx="12" cy="8" r="6"/><path d="M15.477 12.89 17 22l-5-3-5 3 1.523-9.11"/></>,
    Zap:          <><polygon points="13 2 3 14 12 14 11 22 21 10 12 10 13 2"/></>,
    X:            <><path d="M18 6 6 18"/><path d="m6 6 12 12"/></>,
    MapPin:       <><path d="M20 10c0 6-8 12-8 12s-8-6-8-12a8 8 0 0 1 16 0z"/><circle cx="12" cy="10" r="3"/></>,
  };
  return (
    <svg xmlns="http://www.w3.org/2000/svg" width={size} height={size} viewBox="0 0 24 24"
      fill="none" stroke={color} strokeWidth={strokeWidth} strokeLinecap="round" strokeLinejoin="round">
      {paths[name]}
    </svg>
  );
};

/* ══ BONUS TIERS ══ */
const BONUS_TIERS = [
  {
    threshold: 1,
    label: "Starter",
    commission: "10%",
    bonus: null,
    bonusLabel: null,
    desc: "per first completed order",
    highlight: false,
  },
  {
    threshold: 10,
    label: "10 referrals",
    commission: "10%",
    bonus: "KES 1,500",
    bonusLabel: "Milestone bonus",
    desc: "+ one-time bonus paid to M-Pesa",
    highlight: false,
  },
  {
    threshold: 25,
    label: "25 referrals",
    commission: "10%",
    bonus: "KES 4,000",
    bonusLabel: "Growth bonus",
    desc: "+ one-time bonus paid to M-Pesa",
    highlight: true,
  },
  {
    threshold: 50,
    label: "50 referrals",
    commission: "12%",
    bonus: "KES 10,000",
    bonusLabel: "Elite bonus",
    desc: "rate upgrade + one-time bonus",
    highlight: false,
  },
];

/* ══ TESTIMONIALS ══ */
const TESTIMONIALS = [
  {
    name: "Cynthia M.",
    handle: "@cynthianairobi",
    platform: "Instagram",
    location: "Kilimani, Nairobi",
    followers: "8,400 followers",
    earned: "KES 14,200",
    period: "first 3 months",
    text: "I posted once about finding my cleaner through JiraniApp and the DMs did not stop. My followers in Nairobi actually need this. I shared my link in my bio and the commissions just built up. Did not have to push hard at all.",
  },
  {
    name: "Brian O.",
    handle: "Langata WhatsApp admin",
    platform: "WhatsApp",
    location: "Langata, Nairobi",
    followers: "340 group members",
    earned: "KES 8,750",
    period: "2 months",
    text: "I manage three estate WhatsApp groups. When I shared my referral link alongside a post about fixing my plumbing, seven people booked the same week. I hit the 10-referral milestone in the first month.",
  },
  {
    name: "Amira K.",
    handle: "Property blogger",
    platform: "Blog",
    location: "Westlands, Nairobi",
    followers: "12,000 monthly readers",
    earned: "KES 31,600",
    period: "first 6 months",
    text: "I write about Nairobi rentals and property management. JiraniApp is exactly what my readers need. I added my link to two posts and it has been earning passively ever since. Hit 50 referrals and the rate upgrade to 12% made a real difference.",
  },
];

/* ══ COMPARISON TABLE ══ */
const COMPARISON = [
  { feature: "Commission rate",         jirani: "10% per order (12% at 50+ referrals)", others: "Flat KES 50–200 per signup" },
  { feature: "Payment method",          jirani: "Direct M-Pesa, no invoicing needed",    others: "Bank transfer, often delayed" },
  { feature: "Payment timeline",        jirani: "Within 7 days of order completion",     others: "Monthly or quarterly batching" },
  { feature: "Minimum payout",          jirani: "None — every qualifying order pays",    others: "KES 1,000–5,000 minimum threshold" },
  { feature: "Milestone bonuses",       jirani: "Up to KES 10,000 at key milestones",    others: "Rarely offered" },
  { feature: "Earnings cap",            jirani: "No cap",                                others: "Often capped per month" },
  { feature: "Referral tracking",       jirani: "Real-time dashboard",                   others: "Monthly email reports only" },
  { feature: "What counts as a referral", jirani: "Completed booking, not just a signup",  others: "Signup only — no order required" },
];

/* ══ EARNING CALCULATOR ══ */
function EarningCalculator() {
  const [referrals, setReferrals] = useState(10);
  const [avgOrder, setAvgOrder]   = useState(2500);

  const rate = referrals >= 50 ? 0.12 : 0.10;
  const commission = Math.round(referrals * avgOrder * rate);

  const bonus =
    referrals >= 50 ? 10000 :
    referrals >= 25 ? 4000  :
    referrals >= 10 ? 1500  : 0;

  const total = commission + bonus;

  const nextTier =
    referrals < 10 ? { threshold: 10, bonus: "KES 1,500", left: 10 - referrals } :
    referrals < 25 ? { threshold: 25, bonus: "KES 4,000", left: 25 - referrals } :
    referrals < 50 ? { threshold: 50, bonus: "KES 10,000 + 12% rate", left: 50 - referrals } :
    null;

  return (
    <div style={{ padding: "clamp(24px,4vw,36px)", borderRadius: 20, background: T.g50, border: `1.5px solid ${T.g100}` }}>
      <div style={{ display: "inline-flex", alignItems: "center", gap: 7, color: T.g600, fontSize: 11, fontWeight: 600, letterSpacing: "0.09em", textTransform: "uppercase", marginBottom: 14 }}>
        <Icon name="BarChart" size={13} color={T.g500} strokeWidth={2} />
        Earnings estimator
      </div>
      <h3 style={{ fontFamily: T.serif, fontSize: "clamp(20px,3vw,26px)", fontWeight: 400, color: T.n900, margin: "0 0 6px", letterSpacing: "-0.02em" }}>
        See what you could <em style={{ fontStyle: "italic", color: T.g600 }}>earn</em>
      </h3>
      <p style={{ fontSize: 14, color: T.n600, lineHeight: 1.65, margin: "0 0 28px" }}>
        Adjust the sliders below. Commission is 10% per referral, rising to 12% once you hit 50 referrals.
        Milestone bonuses are added automatically at 10, 25, and 50 referrals.
      </p>

      <div style={{ display: "flex", flexDirection: "column", gap: 24, marginBottom: 28 }}>
        <div>
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "baseline", marginBottom: 10 }}>
            <label style={{ fontSize: 13, fontWeight: 600, color: T.n700 }}>Referrals per month</label>
            <span style={{ fontFamily: T.serif, fontSize: 22, fontWeight: 400, color: T.n900, letterSpacing: "-0.02em" }}>{referrals}</span>
          </div>
          <input type="range" min={1} max={80} value={referrals} onChange={e => setReferrals(Number(e.target.value))}
            style={{ width: "100%", accentColor: T.g500, height: 4, cursor: "pointer" }} />
          <div style={{ display: "flex", justifyContent: "space-between", fontSize: 11, color: T.n400, marginTop: 4 }}>
            <span>1</span><span>80</span>
          </div>
        </div>

        <div>
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "baseline", marginBottom: 10 }}>
            <label style={{ fontSize: 13, fontWeight: 600, color: T.n700 }}>Average first order value (KES)</label>
            <span style={{ fontFamily: T.serif, fontSize: 22, fontWeight: 400, color: T.n900, letterSpacing: "-0.02em" }}>{avgOrder.toLocaleString()}</span>
          </div>
          <input type="range" min={500} max={10000} step={500} value={avgOrder} onChange={e => setAvgOrder(Number(e.target.value))}
            style={{ width: "100%", accentColor: T.g500, height: 4, cursor: "pointer" }} />
          <div style={{ display: "flex", justifyContent: "space-between", fontSize: 11, color: T.n400, marginTop: 4 }}>
            <span>KES 500</span><span>KES 10,000</span>
          </div>
        </div>
      </div>

      {/* Milestone nudge */}
      {nextTier && (
        <div style={{ marginBottom: 16, padding: "11px 14px", borderRadius: 10, background: T.goldBg, border: `1px solid ${T.goldBorder}`, display: "flex", alignItems: "center", gap: 9 }}>
          <Icon name="Gift" size={15} color={T.gold} strokeWidth={1.8} />
          <span style={{ fontSize: 13, color: T.gold, fontWeight: 500 }}>
            {nextTier.left} more referral{nextTier.left !== 1 ? "s" : ""} to unlock a {nextTier.bonus} bonus
          </span>
        </div>
      )}

      {/* Result */}
      <div style={{ padding: "20px 24px", borderRadius: 14, background: T.white, border: `1px solid ${T.g200}` }}>
        <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(160px,1fr))", gap: 16, marginBottom: bonus > 0 ? 16 : 0 }}>
          <div>
            <div style={{ fontSize: 12, color: T.n400, fontWeight: 500, marginBottom: 4 }}>Commission ({rate * 100}%)</div>
            <div style={{ fontFamily: T.serif, fontSize: "clamp(24px,5vw,34px)", fontWeight: 400, color: T.n900, letterSpacing: "-0.03em", lineHeight: 1 }}>
              KES {commission.toLocaleString()}
            </div>
          </div>
          {bonus > 0 && (
            <div>
              <div style={{ fontSize: 12, color: T.gold, fontWeight: 600, marginBottom: 4 }}>Milestone bonus</div>
              <div style={{ fontFamily: T.serif, fontSize: "clamp(24px,5vw,34px)", fontWeight: 400, color: T.gold, letterSpacing: "-0.03em", lineHeight: 1 }}>
                + KES {bonus.toLocaleString()}
              </div>
            </div>
          )}
        </div>
        {bonus > 0 && (
          <div style={{ borderTop: `1px solid ${T.n100}`, paddingTop: 14, display: "flex", alignItems: "center", justifyContent: "space-between", flexWrap: "wrap", gap: 12 }}>
            <div>
              <div style={{ fontSize: 12, color: T.n400, fontWeight: 500, marginBottom: 3 }}>Total estimated earnings</div>
              <div style={{ fontFamily: T.serif, fontSize: "clamp(28px,5.5vw,40px)", fontWeight: 400, color: T.g600, letterSpacing: "-0.04em", lineHeight: 1 }}>
                KES {total.toLocaleString()}
              </div>
            </div>
            <div style={{ display: "flex", alignItems: "center", gap: 7, fontSize: 13, color: T.g700, background: T.g50, border: `1px solid ${T.g100}`, borderRadius: 10, padding: "8px 14px" }}>
              <Icon name="Wallet" size={15} color={T.g500} strokeWidth={1.8} />
              Paid via M-Pesa
            </div>
          </div>
        )}
        {bonus === 0 && (
          <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", flexWrap: "wrap", gap: 12 }}>
            <div style={{ fontSize: 12, color: T.n400, marginTop: 4 }}>
              {referrals} referrals × KES {avgOrder.toLocaleString()} × 10%
            </div>
            <div style={{ display: "flex", alignItems: "center", gap: 7, fontSize: 13, color: T.g700, background: T.g50, border: `1px solid ${T.g100}`, borderRadius: 10, padding: "8px 14px" }}>
              <Icon name="Wallet" size={15} color={T.g500} strokeWidth={1.8} />
              Paid via M-Pesa
            </div>
          </div>
        )}
      </div>

      <p style={{ fontSize: 12, color: T.n400, margin: "12px 0 0", lineHeight: 1.6 }}>
        Estimates are illustrative. Actual earnings depend on the value of each referred user's first completed booking.
        Commission and bonuses are paid within 7 days of qualifying orders completing.
      </p>
    </div>
  );
}

/* ══ BONUS TIER CARD ══ */
function BonusTierCard({ tier }) {
  return (
    <div style={{
      flex: "1 1 160px", padding: "22px 18px", borderRadius: 16,
      border: tier.highlight ? `2px solid ${T.g500}` : `1.5px solid ${T.n150}`,
      background: tier.highlight ? T.g500 : T.white,
      display: "flex", flexDirection: "column", gap: 12, position: "relative",
    }}>
      {tier.highlight && (
        <div style={{ position: "absolute", top: -11, left: "50%", transform: "translateX(-50%)", background: T.g700, color: T.white, fontSize: 10, fontWeight: 700, letterSpacing: "0.08em", padding: "3px 12px", borderRadius: 99, whiteSpace: "nowrap", fontFamily: T.sans }}>
          MOST POPULAR
        </div>
      )}
      <div style={{ fontSize: 11, fontWeight: 700, color: tier.highlight ? T.g100 : T.g500, letterSpacing: "0.1em", textTransform: "uppercase" }}>{tier.label}</div>
      <div>
        <div style={{ fontFamily: T.serif, fontSize: "clamp(26px,4vw,32px)", fontWeight: 400, color: tier.highlight ? T.white : T.n900, letterSpacing: "-0.03em", lineHeight: 1 }}>{tier.commission}</div>
        <div style={{ fontSize: 12, color: tier.highlight ? T.g200 : T.n400, marginTop: 3 }}>commission rate</div>
      </div>
      {tier.bonus ? (
        <div style={{ padding: "10px 12px", borderRadius: 10, background: tier.highlight ? T.g400 : T.goldBg, border: `1px solid ${tier.highlight ? T.g400 : T.goldBorder}` }}>
          <div style={{ fontSize: 10, fontWeight: 700, color: tier.highlight ? T.g100 : T.gold, letterSpacing: "0.08em", textTransform: "uppercase", marginBottom: 2 }}>{tier.bonusLabel}</div>
          <div style={{ fontFamily: T.serif, fontSize: 20, fontWeight: 400, color: tier.highlight ? T.white : T.gold, letterSpacing: "-0.02em" }}>{tier.bonus}</div>
        </div>
      ) : (
        <div style={{ padding: "10px 12px", borderRadius: 10, background: T.n50, border: `1px solid ${T.n150}` }}>
          <div style={{ fontSize: 13, color: T.n400 }}>No bonus yet</div>
          <div style={{ fontSize: 12, color: T.n300, marginTop: 2 }}>Unlock at 10 referrals</div>
        </div>
      )}
      <div style={{ fontSize: 12, color: tier.highlight ? T.g100 : T.n500, lineHeight: 1.5 }}>{tier.desc}</div>
    </div>
  );
}

/* ══ TESTIMONIAL ══ */
function TestimonialCard({ t }) {
  const initials = t.name.split(" ").map(w => w[0]).slice(0, 2).join("");
  const palette = [
    { bg: T.g50, text: T.g700, border: T.g100 },
    { bg: "#eff6ff", text: "#1d4ed8", border: "#bfdbfe" },
    { bg: "#fdf4ff", text: "#7e22ce", border: "#e9d5ff" },
  ];
  const c = palette[t.name.charCodeAt(0) % palette.length];
  return (
    <div style={{ padding: "24px", borderRadius: 16, border: `1px solid ${T.n150}`, background: T.white, display: "flex", flexDirection: "column", gap: 16 }}>
      <div style={{ display: "flex", gap: 8, alignItems: "center", flexWrap: "wrap" }}>
        <span style={{ fontSize: 11, fontWeight: 600, color: T.g600, background: T.g50, border: `1px solid ${T.g100}`, borderRadius: 99, padding: "3px 9px" }}>{t.platform}</span>
        <span style={{ fontSize: 11, color: T.n400 }}>{t.followers}</span>
      </div>
      <p style={{ fontSize: 14, color: T.n600, lineHeight: 1.8, margin: 0, fontStyle: "italic" }}>"{t.text}"</p>
      <div style={{ borderTop: `1px solid ${T.n100}`, paddingTop: 16, display: "flex", alignItems: "center", justifyContent: "space-between", gap: 12, flexWrap: "wrap" }}>
        <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
          <div style={{ width: 36, height: 36, borderRadius: "50%", background: c.bg, border: `1.5px solid ${c.border}`, display: "flex", alignItems: "center", justifyContent: "center", fontSize: 12, fontWeight: 600, color: c.text, fontFamily: T.sans, flexShrink: 0 }}>{initials}</div>
          <div>
            <div style={{ fontSize: 13, fontWeight: 600, color: T.n900 }}>{t.name}</div>
            <div style={{ fontSize: 11, color: T.n400 }}>{t.handle} · {t.location}</div>
          </div>
        </div>
        <div style={{ textAlign: "right" }}>
          <div style={{ fontFamily: T.serif, fontSize: 18, color: T.g600, fontWeight: 400, letterSpacing: "-0.02em" }}>{t.earned}</div>
          <div style={{ fontSize: 11, color: T.n400 }}>{t.period}</div>
        </div>
      </div>
    </div>
  );
}

/* ══ AFFILIATE TYPE CARD ══ */
function AffiliateTypeCard({ icon, title, desc, examples }) {
  return (
    <div style={{ padding: "24px", borderRadius: 16, border: `1px solid ${T.n150}`, background: T.white, display: "flex", flexDirection: "column", gap: 14 }}>
      <div style={{ width: 44, height: 44, borderRadius: 12, background: T.g50, border: `1.5px solid ${T.g100}`, display: "flex", alignItems: "center", justifyContent: "center" }}>
        <Icon name={icon} size={20} color={T.g500} strokeWidth={1.6} />
      </div>
      <div>
        <div style={{ fontSize: 15, fontWeight: 600, color: T.n900, marginBottom: 6 }}>{title}</div>
        <p style={{ fontSize: 14, color: T.n600, lineHeight: 1.7, margin: "0 0 12px" }}>{desc}</p>
        <div style={{ display: "flex", flexWrap: "wrap", gap: 6 }}>
          {examples.map((e, i) => (
            <span key={i} style={{ fontSize: 12, color: T.g700, background: T.g50, border: `1px solid ${T.g100}`, borderRadius: 99, padding: "3px 10px", fontWeight: 500 }}>{e}</span>
          ))}
        </div>
      </div>
    </div>
  );
}

/* ══ STEP ══ */
function Step({ num, title, desc }) {
  return (
    <div style={{ display: "flex", gap: 18, padding: "20px 0", borderBottom: `1px solid ${T.n100}` }}>
      <div style={{ width: 36, height: 36, borderRadius: 10, background: T.g500, display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0, marginTop: 2 }}>
        <span style={{ fontSize: 13, fontWeight: 700, color: T.white, fontFamily: T.sans }}>{num}</span>
      </div>
      <div style={{ flex: 1, paddingTop: 2 }}>
        <div style={{ fontSize: 14, fontWeight: 600, color: T.n900, marginBottom: 4 }}>{title}</div>
        <div style={{ fontSize: 14, color: T.n600, lineHeight: 1.65 }}>{desc}</div>
      </div>
    </div>
  );
}

/* ══ FAQ ══ */
function FaqItem({ q, a }) {
  const [open, setOpen] = useState(false);
  return (
    <div style={{ borderBottom: `1px solid ${T.n100}` }}>
      <button onClick={() => setOpen(o => !o)} style={{ width: "100%", display: "flex", justifyContent: "space-between", alignItems: "center", padding: "17px 0", background: "none", border: "none", cursor: "pointer", fontFamily: T.sans, textAlign: "left", gap: 16 }}>
        <span style={{ fontSize: 14, fontWeight: 600, color: T.n800, lineHeight: 1.4 }}>{q}</span>
        <span style={{ flexShrink: 0, color: T.n400, fontSize: 18 }}>{open ? "−" : "+"}</span>
      </button>
      {open && <p style={{ fontSize: 14, color: T.n600, lineHeight: 1.75, margin: "0 0 16px", paddingRight: 24 }}>{a}</p>}
    </div>
  );
}

/* ══ FORM HELPERS ══ */
function Field({ label, required, hint, children }) {
  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
      <label style={{ fontSize: 13, fontWeight: 600, color: T.n700 }}>
        {label}{required && <span style={{ color: "#e53e3e", marginLeft: 3 }}>*</span>}
      </label>
      {children}
      {hint && <span style={{ fontSize: 12, color: T.n400, lineHeight: 1.5 }}>{hint}</span>}
    </div>
  );
}

const iStyle = (focused) => ({
  width: "100%", padding: "12px 14px", boxSizing: "border-box",
  border: `1.5px solid ${focused ? T.g500 : T.n150}`, borderRadius: 10,
  fontSize: 14, fontFamily: T.sans, color: T.n900,
  background: T.white, outline: "none", transition: "border-color .15s",
});

const selStyle = {
  width: "100%", padding: "12px 14px", boxSizing: "border-box",
  border: `1.5px solid ${T.n150}`, borderRadius: 10,
  fontSize: 14, fontFamily: T.sans, color: T.n700,
  background: T.white, outline: "none", appearance: "none",
  backgroundImage: `url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='12' height='12' viewBox='0 0 24 24' fill='none' stroke='%238a93a8' stroke-width='2'%3E%3Cpath d='m6 9 6 6 6-6'/%3E%3C/svg%3E")`,
  backgroundRepeat: "no-repeat", backgroundPosition: "right 12px center",
  cursor: "pointer",
};

/* ══ APPLICATION FORM (unchanged logic from original) ══ */
function ApplicationForm() {
  const [form, setForm] = useState({
    full_name: "", email: "", phone: "", affiliate_type: "",
    platform: "", audience_size: "", location: "", motivation: "",
  });
  const [focused,   setFocused]   = useState({});
  const [loading,   setLoading]   = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [error,     setError]     = useState("");

  const set   = (k, v) => setForm(f => ({ ...f, [k]: v }));
  const focus = (k)    => setFocused(f => ({ ...f, [k]: true }));
  const blur  = (k)    => setFocused(f => ({ ...f, [k]: false }));

  const canSubmit = form.full_name.trim() && form.email.trim() &&
    form.phone.trim() && form.affiliate_type && !loading;

  const handleSubmit = async () => {
    setError("");
    setLoading(true);
    const { error: err } = await supabase.from("affiliate_applications").insert({
      full_name:      form.full_name.trim(),
      email:          form.email.trim(),
      phone:          form.phone.trim(),
      affiliate_type: form.affiliate_type,
      platform:       form.platform.trim()  || null,
      audience_size:  form.audience_size     || null,
      location:       form.location.trim()   || null,
      motivation:     form.motivation.trim() || null,
    });
    setLoading(false);
    if (err) { setError("Something went wrong. Please try again or email us directly."); return; }
    setSubmitted(true);
  };

  if (submitted) {
    return (
      <div style={{ textAlign: "center", padding: "48px 24px" }}>
        <div style={{ width: 64, height: 64, borderRadius: "50%", background: T.g50, border: `1.5px solid ${T.g100}`, display: "flex", alignItems: "center", justifyContent: "center", margin: "0 auto 20px" }}>
          <Icon name="CircleCheck" size={28} color={T.g500} strokeWidth={1.6} />
        </div>
        <h3 style={{ fontFamily: T.serif, fontSize: "clamp(22px,4vw,28px)", fontWeight: 400, color: T.n900, margin: "0 0 10px", letterSpacing: "-0.02em" }}>
          Application <em style={{ fontStyle: "italic", color: T.g600 }}>received</em>
        </h3>
        <p style={{ fontSize: 14, color: T.n600, lineHeight: 1.75, maxWidth: 380, margin: "0 auto 6px" }}>
          We will review your application and get back to you within 3 business days with your
          unique referral link and onboarding details.
        </p>
        <p style={{ fontSize: 13, color: T.n400, maxWidth: 380, margin: "0 auto" }}>
          Check your email at <strong style={{ color: T.n700 }}>{form.email}</strong> for a confirmation.
        </p>
      </div>
    );
  }

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 20 }}>
      <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(200px, 1fr))", gap: 16 }}>
        <Field label="Full name" required>
          <input value={form.full_name} onChange={e => set("full_name", e.target.value)}
            placeholder="Your full name" style={iStyle(focused.full_name)}
            onFocus={() => focus("full_name")} onBlur={() => blur("full_name")} />
        </Field>
        <Field label="Email address" required>
          <input type="email" value={form.email} onChange={e => set("email", e.target.value)}
            placeholder="you@example.com" style={iStyle(focused.email)}
            onFocus={() => focus("email")} onBlur={() => blur("email")} />
        </Field>
      </div>

      <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(200px, 1fr))", gap: 16 }}>
        <Field label="Phone number (M-Pesa)" required hint="This is where your commission will be paid.">
          <input type="tel" value={form.phone} onChange={e => set("phone", e.target.value)}
            placeholder="+254 7XX XXX XXX" style={iStyle(focused.phone)}
            onFocus={() => focus("phone")} onBlur={() => blur("phone")} />
        </Field>
        <Field label="How will you refer people?" required>
          <select value={form.affiliate_type} onChange={e => set("affiliate_type", e.target.value)} style={selStyle}
            onFocus={e => (e.target.style.borderColor = T.g500)}
            onBlur={e  => (e.target.style.borderColor = T.n150)}>
            <option value="">Select your method</option>
            <option value="social_media">Social media (Instagram, TikTok, X)</option>
            <option value="whatsapp_community">WhatsApp group or community</option>
            <option value="blog_website">Blog or website</option>
            <option value="youtube">YouTube</option>
            <option value="word_of_mouth">Word of mouth and personal network</option>
            <option value="estate_community">Estate or neighbourhood community</option>
            <option value="business_network">Business or professional network</option>
            <option value="other">Other</option>
          </select>
        </Field>
      </div>

      <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(200px, 1fr))", gap: 16 }}>
        <Field label="Platform or handle" hint="e.g. @yourhandle on Instagram, your blog URL, your WhatsApp group name">
          <input value={form.platform} onChange={e => set("platform", e.target.value)}
            placeholder="@yourhandle or your website" style={iStyle(focused.platform)}
            onFocus={() => focus("platform")} onBlur={() => blur("platform")} />
        </Field>
        <Field label="Approximate audience or reach">
          <select value={form.audience_size} onChange={e => set("audience_size", e.target.value)} style={selStyle}
            onFocus={e => (e.target.style.borderColor = T.g500)}
            onBlur={e  => (e.target.style.borderColor = T.n150)}>
            <option value="">Select range</option>
            <option value="under_500">Under 500</option>
            <option value="500_2000">500 to 2,000</option>
            <option value="2000_10000">2,000 to 10,000</option>
            <option value="10000_50000">10,000 to 50,000</option>
            <option value="50000+">Over 50,000</option>
          </select>
        </Field>
      </div>

      <Field label="Your city or area" hint="Helps us match you with relevant local content and promotions.">
        <input value={form.location} onChange={e => set("location", e.target.value)}
          placeholder="e.g. Westlands, Nairobi"
          style={{ ...iStyle(focused.location), maxWidth: 320 }}
          onFocus={() => focus("location")} onBlur={() => blur("location")} />
      </Field>

      <Field label="Why do you want to join the programme?">
        <textarea value={form.motivation} onChange={e => set("motivation", e.target.value.slice(0, 500))}
          placeholder="Tell us a bit about yourself, your audience, and why you think JiraniApp is a good fit."
          rows={4}
          style={{ ...iStyle(focused.motivation), resize: "vertical", lineHeight: 1.6 }}
          onFocus={() => focus("motivation")} onBlur={() => blur("motivation")} />
        <span style={{ fontSize: 12, color: T.n400, textAlign: "right" }}>{form.motivation.length} / 500</span>
      </Field>

      <div style={{ display: "flex", gap: 10, padding: "14px 16px", borderRadius: 10, background: T.amberBg, border: `1px solid ${T.amberBorder}` }}>
        <Icon name="Info" size={16} color={T.amber} strokeWidth={1.8} />
        <p style={{ fontSize: 13, color: "#78350f", margin: 0, lineHeight: 1.6 }}>
          You earn <strong>10% of the first completed order</strong> placed by each person you refer,
          paid directly to your M-Pesa within 7 days. Milestone bonuses unlock at 10, 25, and 50 referrals.
          There is no cap on the number of referrals you can make.
        </p>
      </div>

      {error && (
        <p style={{ fontSize: 13, color: "#c0392b", margin: 0, padding: "10px 14px", background: "#fdecea", borderRadius: 8 }}>{error}</p>
      )}

      <button onClick={handleSubmit} disabled={!canSubmit}
        style={{
          padding: "14px 0", background: canSubmit ? T.g500 : T.n150,
          color: canSubmit ? T.white : T.n400, border: "none", borderRadius: 12,
          fontSize: 15, fontWeight: 600, fontFamily: T.sans,
          cursor: canSubmit ? "pointer" : "not-allowed",
          letterSpacing: "0.01em", transition: "background .15s",
          display: "flex", alignItems: "center", justifyContent: "center", gap: 8,
        }}
        onMouseEnter={e => { if (canSubmit) e.currentTarget.style.background = T.g600; }}
        onMouseLeave={e => { if (canSubmit) e.currentTarget.style.background = T.g500; }}>
        {loading ? "Submitting..." : <>Apply to the programme <Icon name="ArrowRight" size={16} color={canSubmit ? T.white : T.n400} strokeWidth={2} /></>}
      </button>

      <p style={{ fontSize: 12, color: T.n400, textAlign: "center", margin: 0, lineHeight: 1.6 }}>
        Have questions first? Email{" "}
        <a href="mailto:affiliates@jiraniapp.com" style={{ color: T.g600, textDecoration: "none", borderBottom: `1px solid ${T.g200}` }}>
          affiliates@jiraniapp.com
        </a>
      </p>
    </div>
  );
}

/* ══ MAIN PAGE ══ */
export default function AffiliatesPage() {
  useFonts();
  const navigate = useNavigate();

  const faqs = [
    { q: "When and how do I get paid?", a: "You are paid via M-Pesa within 7 days of each qualifying order completing. A qualifying order is the first completed booking placed by a user you referred through your unique link. There is no minimum payout threshold." },
    { q: "How do the milestone bonuses work?", a: "Milestone bonuses are one-time payments triggered when your cumulative referral count crosses a threshold. At 10 referrals you receive KES 1,500. At 25 you receive KES 4,000. At 50 you receive KES 10,000 and your commission rate upgrades permanently to 12%. Bonuses are paid to your M-Pesa within 7 days of crossing each threshold." },
    { q: "Is there a limit to how many people I can refer?", a: "No. There is no cap on referrals. The more people you successfully refer, the more you earn, and the higher your milestone bonus tier. Each referred user generates one commission payout based on their first completed order." },
    { q: "What counts as a qualifying order?", a: "A qualifying order is a booking that is placed by a new user who signed up through your referral link and is then completed successfully by a JiraniPro. Cancelled bookings do not qualify. The commission is based on the value of that first completed order." },
    { q: "How do I know when someone has used my link?", a: "Once approved, you will have access to a simple affiliate dashboard showing clicks, signups, completed orders, pending commissions, and your current milestone progress. You will also receive an M-Pesa notification each time a commission is processed." },
    { q: "Do people I refer need to be new to JiraniApp?", a: "Yes. The referral commission applies only to users who are new to JiraniApp and sign up through your unique link. Existing users do not generate a commission." },
    { q: "What if I refer a JiraniPro rather than a client?", a: "The programme is open to referring both clients and JiraniPros. When a referred JiraniPro completes their first job, the commission is calculated based on the value of that booking and counts toward your milestone total." },
    { q: "Can I use paid advertising to promote my referral link?", a: "You may use paid advertising to promote JiraniApp through your referral link, but you may not bid on JiraniApp branded keywords or use our logos and assets without prior written approval. Contact us if you plan to run paid campaigns." },
    { q: "How long does approval take?", a: "We review applications within 3 business days. Once approved, you will receive your unique referral link and onboarding instructions by email." },
  ];

  return (
    <div style={{ fontFamily: T.sans, background: T.white, minHeight: "100vh", color: T.n900 }}>
      <style>{`* { box-sizing: border-box; } select { font-family: ${T.sans}; }`}</style>

      {/* ── Nav ── */}
      <div style={{ padding: "20px 24px", borderBottom: `1px solid ${T.n100}` }}>
        <button onClick={() => navigate(-1)} style={{ display: "inline-flex", alignItems: "center", gap: 6, background: "none", border: "none", cursor: "pointer", color: T.n400, fontSize: 13, fontFamily: T.sans, padding: 0 }}
          onMouseEnter={e => (e.currentTarget.style.color = T.n800)}
          onMouseLeave={e => (e.currentTarget.style.color = T.n400)}>
          <Icon name="ArrowLeft" size={14} strokeWidth={2} />
          Back
        </button>
      </div>

      {/* ══ HERO ══ */}
      <div style={{ padding: "clamp(48px,7vw,88px) 20px clamp(40px,5vw,60px)", borderBottom: `1px solid ${T.n100}` }}>
        <div style={{ maxWidth: 720, margin: "0 auto" }}>
          <div style={{ display: "inline-flex", alignItems: "center", gap: 7, color: T.g600, fontSize: 12, fontWeight: 600, letterSpacing: "0.09em", textTransform: "uppercase", marginBottom: 20 }}>
            <Icon name="Percent" size={14} color={T.g500} strokeWidth={2.2} />
            Affiliate programme
          </div>
          <h1 style={{ fontFamily: T.serif, fontSize: "clamp(38px,7vw,62px)", fontWeight: 400, margin: "0 0 18px", lineHeight: 1.07, color: T.n900, letterSpacing: "-0.03em", maxWidth: 580 }}>
            Refer. Earn.{" "}
            <em style={{ fontStyle: "italic", color: T.g600 }}>Get paid.</em>
          </h1>
          <p style={{ fontSize: "clamp(15px,2vw,17px)", color: T.n600, lineHeight: 1.75, margin: "0 0 8px", maxWidth: 520 }}>
            Share JiraniApp with your network and earn 10% of the first completed order
            from every person you refer, paid directly to your M-Pesa. Hit milestones and
            unlock cash bonuses up to KES 10,000 plus a rate upgrade to 12%.
          </p>
          <p style={{ fontSize: 14, color: T.n400, lineHeight: 1.65, margin: "0 0 28px", maxWidth: 480 }}>
            No cap. No minimum payout. No invoicing.
          </p>

          {/* Badges */}
          <div style={{ display: "flex", flexWrap: "wrap", gap: 10, marginBottom: 32 }}>
            {[
              { icon: "Percent", text: "10% per referral" },
              { icon: "Gift",    text: "Bonuses up to KES 10,000" },
              { icon: "Wallet",  text: "Paid via M-Pesa" },
              { icon: "Clock",   text: "Within 7 days" },
              { icon: "Shield",  text: "No cap on earnings" },
            ].map(b => (
              <div key={b.text} style={{ display: "inline-flex", alignItems: "center", gap: 7, fontSize: 13, fontWeight: 600, color: T.g700, background: T.g50, border: `1px solid ${T.g100}`, borderRadius: 99, padding: "7px 13px" }}>
                <Icon name={b.icon} size={14} color={T.g500} strokeWidth={1.9} />
                {b.text}
              </div>
            ))}
          </div>

          <a href="#apply" style={{
            display: "inline-flex", alignItems: "center", gap: 8,
            padding: "14px 28px", background: T.g500, color: T.white,
            border: "none", borderRadius: 12, fontSize: 15, fontWeight: 600,
            fontFamily: T.sans, cursor: "pointer", letterSpacing: "0.01em",
            textDecoration: "none",
          }}
            onMouseEnter={e => (e.currentTarget.style.background = T.g600)}
            onMouseLeave={e => (e.currentTarget.style.background = T.g500)}>
            Apply now
            <Icon name="ArrowRight" size={16} color={T.white} strokeWidth={2} />
          </a>
        </div>
      </div>

      <div style={{ maxWidth: 720, margin: "0 auto", padding: "0 20px clamp(80px,10vw,120px)" }}>

        {/* ══ SOCIAL PROOF STRIP ══ */}
        <div style={{ paddingTop: "clamp(36px,5vw,52px)", marginBottom: "clamp(48px,7vw,72px)" }}>
          <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(160px,1fr))", gap: 0, borderRadius: 16, border: `1px solid ${T.n150}`, overflow: "hidden" }}>
            {[
              { value: "620+",      label: "Active affiliates" },
              { value: "KES 2.1M+", label: "Commission paid out to date" },
              { value: "4.2 days",  label: "Average payout time" },
              { value: "78%",       label: "Affiliates who hit 10 referrals within 60 days" },
            ].map((s, i, arr) => (
              <div key={i} style={{ padding: "24px 20px", textAlign: "center", borderRight: i < arr.length - 1 ? `1px solid ${T.n150}` : "none", background: T.white }}>
                <div style={{ fontFamily: T.serif, fontSize: "clamp(22px,4vw,28px)", fontWeight: 400, color: T.g600, letterSpacing: "-0.03em", lineHeight: 1, marginBottom: 6 }}>{s.value}</div>
                <div style={{ fontSize: 12, color: T.n400, lineHeight: 1.45 }}>{s.label}</div>
              </div>
            ))}
          </div>
        </div>

        {/* ══ EARNING CALCULATOR ══ */}
        <div style={{ marginBottom: "clamp(48px,7vw,72px)" }}>
          <EarningCalculator />
        </div>

        {/* ══ BONUS TIERS ══ */}
        <div style={{ marginBottom: "clamp(48px,7vw,72px)" }}>
          <span style={{ fontSize: 11, fontWeight: 600, color: T.g500, letterSpacing: "0.1em", textTransform: "uppercase" }}>Milestone bonuses</span>
          <h2 style={{ fontFamily: T.serif, fontSize: "clamp(24px,4vw,32px)", fontWeight: 400, color: T.n900, margin: "8px 0 8px", letterSpacing: "-0.03em", lineHeight: 1.2 }}>
            The more you refer, the more you <em style={{ fontStyle: "italic", color: T.g600 }}>unlock</em>
          </h2>
          <p style={{ fontSize: 15, color: T.n400, margin: "0 0 24px", lineHeight: 1.65 }}>
            Every referral counts toward your milestone total. Bonuses are paid once and the 12% rate upgrade at 50 referrals is permanent, applying to every qualifying order from that point forward.
          </p>
          <div style={{ display: "flex", gap: 12, flexWrap: "wrap", alignItems: "stretch" }}>
            {BONUS_TIERS.map((tier, i) => <BonusTierCard key={i} tier={tier} />)}
          </div>
          <div style={{ marginTop: 16, padding: "13px 16px", borderRadius: 10, background: T.n50, border: `1px solid ${T.n150}` }}>
            <p style={{ fontSize: 13, color: T.n600, margin: 0, lineHeight: 1.65 }}>
              Milestone counts are cumulative across all time, not reset monthly. Once you unlock a tier, the benefit is permanent. The 12% commission rate at 50 referrals applies to all qualifying orders from that point forward, not just the 50th.
            </p>
          </div>
        </div>

        {/* ══ TESTIMONIALS ══ */}
        <div style={{ marginBottom: "clamp(48px,7vw,72px)" }}>
          <span style={{ fontSize: 11, fontWeight: 600, color: T.g500, letterSpacing: "0.1em", textTransform: "uppercase" }}>From affiliates</span>
          <h2 style={{ fontFamily: T.serif, fontSize: "clamp(24px,4vw,32px)", fontWeight: 400, color: T.n900, margin: "8px 0 8px", letterSpacing: "-0.03em", lineHeight: 1.2 }}>
            What affiliates are <em style={{ fontStyle: "italic", color: T.g600 }}>earning</em>
          </h2>
          <p style={{ fontSize: 15, color: T.n400, margin: "0 0 22px", lineHeight: 1.65 }}>
            From creators, community admins, and bloggers already in the programme.
          </p>
          <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(290px,1fr))", gap: 14 }}>
            {TESTIMONIALS.map((t, i) => <TestimonialCard key={i} t={t} />)}
          </div>
        </div>

        {/* ══ COMPARISON ══ */}
        <div style={{ marginBottom: "clamp(48px,7vw,72px)" }}>
          <span style={{ fontSize: 11, fontWeight: 600, color: T.g500, letterSpacing: "0.1em", textTransform: "uppercase" }}>How we compare</span>
          <h2 style={{ fontFamily: T.serif, fontSize: "clamp(24px,4vw,32px)", fontWeight: 400, color: T.n900, margin: "8px 0 8px", letterSpacing: "-0.03em", lineHeight: 1.2 }}>
            Not all affiliate programmes are <em style={{ fontStyle: "italic", color: T.g600 }}>equal</em>
          </h2>
          <p style={{ fontSize: 15, color: T.n400, margin: "0 0 22px", lineHeight: 1.65 }}>
            Most pay you a flat signup fee for a person who may never even complete a booking. We pay you a percentage of actual real money spent.
          </p>
          <div style={{ borderRadius: 16, border: `1px solid ${T.n150}`, overflow: "hidden" }}>
            {/* Header */}
            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr", background: T.n50, borderBottom: `1px solid ${T.n150}` }}>
              <div style={{ padding: "14px 16px", fontSize: 12, fontWeight: 700, color: T.n400, letterSpacing: "0.06em", textTransform: "uppercase" }}>Feature</div>
              <div style={{ padding: "14px 16px", fontSize: 12, fontWeight: 700, color: T.g600, letterSpacing: "0.06em", textTransform: "uppercase", background: T.g50, borderLeft: `1px solid ${T.g100}`, borderRight: `1px solid ${T.g100}` }}>JiraniApp</div>
              <div style={{ padding: "14px 16px", fontSize: 12, fontWeight: 700, color: T.n400, letterSpacing: "0.06em", textTransform: "uppercase" }}>Typical alternatives</div>
            </div>
            {COMPARISON.map((row, i) => (
              <div key={i} style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr", borderBottom: i < COMPARISON.length - 1 ? `1px solid ${T.n100}` : "none" }}>
                <div style={{ padding: "14px 16px", fontSize: 13, fontWeight: 600, color: T.n700, borderRight: `1px solid ${T.n100}`, display: "flex", alignItems: "center" }}>{row.feature}</div>
                <div style={{ padding: "14px 16px", fontSize: 13, color: T.g700, background: T.g50, borderRight: `1px solid ${T.g100}`, display: "flex", alignItems: "center", gap: 8 }}>
                  <Icon name="CheckCircle" size={14} color={T.g500} strokeWidth={2} style={{ flexShrink: 0 }} />
                  {row.jirani}
                </div>
                <div style={{ padding: "14px 16px", fontSize: 13, color: T.n500, display: "flex", alignItems: "center" }}>{row.others}</div>
              </div>
            ))}
          </div>
        </div>

        {/* ══ WHO CAN JOIN ══ */}
        <div style={{ marginBottom: "clamp(48px,7vw,72px)" }}>
          <span style={{ fontSize: 11, fontWeight: 600, color: T.g500, letterSpacing: "0.1em", textTransform: "uppercase" }}>Who can join</span>
          <h2 style={{ fontFamily: T.serif, fontSize: "clamp(24px,4vw,32px)", fontWeight: 400, color: T.n900, margin: "8px 0 6px", letterSpacing: "-0.03em", lineHeight: 1.2 }}>
            Open to <em style={{ fontStyle: "italic", color: T.g600 }}>everyone</em>
          </h2>
          <p style={{ fontSize: 15, color: T.n400, margin: "0 0 22px", lineHeight: 1.65 }}>
            You do not need a large following or a media platform. If you can recommend JiraniApp to people who need home services, you can earn.
          </p>
          <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(220px, 1fr))", gap: 14 }}>
            <AffiliateTypeCard icon="Instagram" title="Content creators"
              desc="Home, lifestyle, and property-focused creators on Instagram, TikTok, YouTube, or blogs whose audience overlaps with people who need home services."
              examples={["Lifestyle influencers", "Home improvement creators", "Property YouTubers"]} />
            <AffiliateTypeCard icon="Users" title="Community leaders"
              desc="Estate WhatsApp admins, neighbourhood Facebook group managers, and local community organisers with a trusted voice in their area."
              examples={["Estate admins", "Neighbourhood groups", "Local community pages"]} />
            <AffiliateTypeCard icon="Megaphone" title="Bloggers and writers"
              desc="Anyone who writes about Nairobi living, real estate, home improvement, or urban Kenya with a readership that would benefit from JiraniApp."
              examples={["Home improvement blogs", "Nairobi lifestyle sites", "Real estate writers"]} />
            <AffiliateTypeCard icon="Globe" title="Personal networks"
              desc="You do not need a platform. If you have a strong personal or professional network and people trust your recommendations, you can refer and earn."
              examples={["Professionals", "Realtors and agents", "Word of mouth"]} />
          </div>
        </div>

        {/* ══ HOW IT WORKS ══ */}
        <div style={{ marginBottom: "clamp(48px,7vw,72px)" }}>
          <span style={{ fontSize: 11, fontWeight: 600, color: T.g500, letterSpacing: "0.1em", textTransform: "uppercase" }}>How it works</span>
          <h2 style={{ fontFamily: T.serif, fontSize: "clamp(24px,4vw,32px)", fontWeight: 400, color: T.n900, margin: "8px 0 6px", letterSpacing: "-0.03em", lineHeight: 1.2 }}>
            Simple by <em style={{ fontStyle: "italic", color: T.g600 }}>design</em>
          </h2>
          <p style={{ fontSize: 15, color: T.n400, margin: "0 0 8px", lineHeight: 1.65 }}>
            Four steps from application to M-Pesa payment.
          </p>
          <div>
            <Step num="01" title="Apply below" desc="Fill in the application form with your details, how you plan to refer people, and your M-Pesa number. We review every application personally." />
            <Step num="02" title="Get approved and receive your link" desc="Once approved within 3 business days, you receive a unique referral link and access to your affiliate dashboard showing clicks, signups, commissions, and milestone progress." />
            <Step num="03" title="Share with your network" desc="Share your link anywhere, your WhatsApp groups, Instagram bio, YouTube description, blog posts, or directly with friends. Anyone who signs up through your link is tracked to you." />
            <Step num="04" title="Earn 10% per first order, collect bonuses, get paid to M-Pesa" desc="When someone you referred completes their first booking, you earn 10% of that order value. Hit 10 referrals and collect a KES 1,500 bonus. Hit 25 for KES 4,000. Hit 50 for KES 10,000 and a permanent rate upgrade to 12%. All payments arrive on M-Pesa within 7 days." />
          </div>
        </div>

        {/* ══ RULES ══ */}
        <div style={{ marginBottom: "clamp(48px,7vw,72px)", padding: "clamp(24px,4vw,32px)", borderRadius: 18, background: T.n50, border: `1px solid ${T.n150}` }}>
          <span style={{ fontSize: 11, fontWeight: 600, color: T.n400, letterSpacing: "0.1em", textTransform: "uppercase" }}>Programme rules</span>
          <h2 style={{ fontFamily: T.serif, fontSize: "clamp(20px,3vw,26px)", fontWeight: 400, color: T.n900, margin: "8px 0 18px", letterSpacing: "-0.02em", lineHeight: 1.2 }}>
            What to know <em style={{ fontStyle: "italic", color: T.g600 }}>upfront</em>
          </h2>
          <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
            {[
              "Commission is 10% of the referred user's first completed order value, paid via M-Pesa within 7 days.",
              "Milestone bonuses: KES 1,500 at 10 referrals, KES 4,000 at 25, KES 10,000 at 50. The 12% rate upgrade at 50 referrals is permanent.",
              "Only new users who sign up through your unique link qualify. Existing JiraniApp users do not generate a commission.",
              "Cancelled or disputed orders do not qualify. The booking must be completed and confirmed.",
              "There is no minimum payout. Even a single referral generates a payment once it qualifies.",
              "You may not use misleading claims, fake reviews, or incentivised sign-ups to drive referrals.",
              "JiraniApp reserves the right to suspend or terminate affiliate accounts that violate programme rules.",
            ].map((r, i) => (
              <div key={i} style={{ display: "flex", gap: 10, alignItems: "flex-start" }}>
                <Icon name="CheckCircle" size={16} color={T.g500} strokeWidth={2} />
                <span style={{ fontSize: 14, color: T.n600, lineHeight: 1.65 }}>{r}</span>
              </div>
            ))}
          </div>
        </div>

        {/* ══ FAQ ══ */}
        <div style={{ marginBottom: "clamp(48px,7vw,72px)" }}>
          <span style={{ fontSize: 11, fontWeight: 600, color: T.g500, letterSpacing: "0.1em", textTransform: "uppercase" }}>Questions</span>
          <h2 style={{ fontFamily: T.serif, fontSize: "clamp(24px,4vw,32px)", fontWeight: 400, color: T.n900, margin: "8px 0 4px", letterSpacing: "-0.03em", lineHeight: 1.2 }}>
            Common <em style={{ fontStyle: "italic", color: T.g600 }}>questions</em>
          </h2>
          <p style={{ fontSize: 15, color: T.n400, margin: "0 0 8px", lineHeight: 1.65 }}>Everything affiliates ask before joining.</p>
          <div style={{ marginTop: 8 }}>
            {faqs.map((f, i) => <FaqItem key={i} {...f} />)}
          </div>
        </div>

        {/* ══ APPLICATION FORM ══ */}
        <div id="apply" style={{ scrollMarginTop: 32 }}>
          <span style={{ fontSize: 11, fontWeight: 600, color: T.g500, letterSpacing: "0.1em", textTransform: "uppercase" }}>Apply</span>
          <h2 style={{ fontFamily: T.serif, fontSize: "clamp(24px,4vw,32px)", fontWeight: 400, color: T.n900, margin: "8px 0 6px", letterSpacing: "-0.03em", lineHeight: 1.2 }}>
            Join the <em style={{ fontStyle: "italic", color: T.g600 }}>programme</em>
          </h2>
          <p style={{ fontSize: 15, color: T.n400, margin: "0 0 28px", lineHeight: 1.65 }}>
            Applications are reviewed within 3 business days. Approved affiliates receive their unique link and dashboard access by email.
          </p>
          <div style={{ background: T.white, borderRadius: 20, border: `1px solid ${T.n150}`, padding: "clamp(24px,5vw,40px)", boxShadow: "0 4px 24px rgba(15,17,23,.04)" }}>
            <ApplicationForm />
          </div>
        </div>

      </div>
    </div>
  );
}